import React, { useEffect, useState } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Theme
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Scrollbars from 'react-custom-scrollbars-2';

import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import { useAppDispatch, useAppSelector } from './../../redux/hooks';
import { ReactComponent as StarOutlined } from '../../assets/icons/staroutlined.svg';
import { ReactComponent as StarFilled } from '../../assets/icons/starfilled.svg';
import { TabPanel } from '../TwTabPanel/TwTabPanel';
import {
  clearLoadsFilters,
  closeMobileTabDropdown,
  openMobileMenu,
  openMobileTabDropdown,
  selectActiveTab
} from './../../redux/features/navigation/navigationSlice';

import './TwMainView.scss';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { KeyboardArrowDown } from '@mui/icons-material';
import { ItemCounter } from '../TwButtons/ItemCounter/ItemCounter';
import { TwNotificationBanner } from '../TwNotificationBanner/TwNotificationBanner';
import { MobileNavItems } from '../../redux/features/navigation/navigation.model';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import TwTooltip from '../TwTooltip/TwTooltip';

// Props for the Tab components needed for navigation in TwMainView

export interface TwTabItemProps {
  /**
   * Tab item label to display
   */
  label: string;
  /**
   * Components the tab displays
   */
  content: JSX.Element | JSX.Element[];
  /**
   * If true the tab is visible
   */
  visible: boolean;
  /**
   * default
   */
  defaultTab?: boolean;
  /**
   * On mouse enter
   */
  onMouseEnter?: () => void;
  /**
   * Optional item count badge
   */
  tabItemCount?: number;
  /**
   * Start icon
   */
  startIcon?: JSX.Element;
  /**
   * Is a Starred tab
   */
  starred?: boolean;
}

export interface TwTabsProps {
  /**
   * List of tab items and their content
   */
  tabs: TwTabItemProps[];
}

// Props for the TwMainView
export interface TwMainViewProps {
  /**
   * List of main navigation links
   */
  navItems?: TwTabItemProps[];
  /**
   * List of action elements to show to the right of the header view
   */
  actionButtons?: JSX.Element[];
  /**
   * Title for page (if no tabs)
   */
  title?: string;
  /**
   * Main title for the page if tabs are present
   */
  mainTitle?: string;
  /**
   * Action to trigger when a tab is active
   */
  onTabSelected?: (activeTab: string) => void;
  /**
   * defaultTab
   */
  defaultTab?: number;
  /**
   * On tab pinned
   */
  onTabPinned?: (tabName: string) => void;
  /**
   * On tab unpinned
   */
  onTabUnpinned?: (tabName: string) => void;
  headerContent?: JSX.Element;
  children?: React.ReactNode;
  tabsInHeader?: boolean;
  showDropdown?: boolean;
  onlyShowMainTitle?: boolean;
}

const TwMainView: React.FC<TwMainViewProps> = ({
  navItems,
  actionButtons,
  children,
  title,
  mainTitle,
  defaultTab,
  headerContent,
  onTabSelected,
  onTabPinned,
  onTabUnpinned,
  tabsInHeader,
  showDropdown = true,
  onlyShowMainTitle = false
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    data: { userData }
  } = useAuth();
  const isDriverOrCarrier =
    userData?.role === USER_ROLE.DRIVER || userData?.role === USER_ROLE.CARRIER;
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { enableMobileLoadInbox } = useFeatureFlags();
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const { isCalendarOpen, isNotificationBannerAcknowledged, isMobileTabDropdownOpen } =
    useAppSelector((state) => state.navigation);
  const { inboxItemCount = 0 } = useAppSelector((state) => state.inbox);

  const [tabValue, setTabValue] = React.useState(0);
  const [showStar, setShowStar] = useState<number | null>(null);

  useEffect(() => {
    if (defaultTab !== -1 && defaultTab) {
      const activeTab = isDriverOrCarrier ? MobileNavItems.ACTIVE_TRIPS : MobileNavItems.ORDERS;
      setTabValue(defaultTab);
      dispatch(selectActiveTab(activeTab));
    }
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  const handleDropdownToggle = (): void => {
    dispatch(isMobileTabDropdownOpen ? closeMobileTabDropdown() : openMobileTabDropdown());
  };

  const handleItemClick = (event: React.SyntheticEvent, index: number): void => {
    if (navItems) {
      dispatch(selectActiveTab(navItems[index].label as MobileNavItems));
    }
    setTabValue(index);
    dispatch(clearLoadsFilters());
    dispatch(closeMobileTabDropdown());
  };

  const handleDropdownClose = (event: Event): void => {
    dispatch(closeMobileTabDropdown());
  };

  /** Tab item count */
  const itemCountSx = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '0 10px',
    backgroundColor: 'rgba(254, 226, 226, 1)',
    borderRadius: '10px',
    height: '20px',
    color: '#B91C1C',
    fontSize: '12px',
    fontWeight: 500
  };

  const showDesktopTabs = (): JSX.Element => {
    return (
      <Tabs
        variant="standard"
        sx={{
          minHeight: '64px',
          width: '100%',
          alignSelf: 'baseline',

          '& .MuiTabs-scroller': {
            height: '59px'
          },
          '& .MuiTabs-scroller .MuiTabs-indicator': {
            display: mainTitle ? 'none' : 'block',
            backgroundColor: '#3A4DE9',
            height: '1px',
            border: '1.3px solid #3A4DE9',
            borderRadius: '10px 10px 0 0'
          },
          '& .Mui-selected': {
            textTransform: 'none',
            color: '#1d212c !important'
          },
          '& .MuiTabs-indicator': {
            display: navItems?.length === 1 ? 'none' : 'block'
          }
        }}
        value={tabValue}
        onChange={handleChange}
        aria-label="TwTabs"
        role="TwTabs"
      >
        {desktopTabs()}
      </Tabs>
    );
  };

  const desktopTabs = (): JSX.Element[] | undefined => {
    const items = navItems?.map((tab: TwTabItemProps, index: number): JSX.Element => {
      if (!!tab.visible) {
        // We assume we'll show new tabs design only if we have a main title for now
        if (mainTitle) {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '36px',
                border: 'none',
                borderRadius: '25px',
                background: tabValue !== index ? 'none' : '#F3F4F6',
                padding: '10px 18px 10px 8px',
                marginTop: '12px'
              }}
              component={'div'}
              onMouseOver={(e): void => {
                setShowStar(index);
              }}
              onMouseLeave={(e): void => {
                setShowStar(null);
              }}
            >
              {!tab.starred && showStar !== index ? (
                <IconButton
                  key={`section-icon-${index}`}
                  onClick={(): void => setTabValue(index)}
                  disableRipple
                  sx={{
                    '& svg path': { fill: tabValue !== index ? '#9CA3AF' : '#3A4DE9' }
                  }}
                >
                  {tab.startIcon}
                </IconButton>
              ) : (
                <TwTooltip
                  title={tab.starred ? 'Remove favorite' : 'Make favorite'}
                  placement="bottom"
                >
                  <IconButton
                    key={`star-${index}`}
                    onClick={(): void => {
                      !tab.starred && onTabPinned && onTabPinned(tab.label);
                      tab.starred && onTabUnpinned && onTabUnpinned(tab.label);
                    }}
                    disableRipple
                  >
                    {tab.starred ? <StarFilled /> : <StarOutlined />}
                  </IconButton>
                </TwTooltip>
              )}

              <Tab
                label={tab.label}
                icon={tab.tabItemCount ? <div style={itemCountSx}>{tab.tabItemCount}</div> : ''}
                iconPosition="end"
                key={index}
                sx={{
                  fontFamily: 'Poppins',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  opacity: '100%',
                  padding: '0',
                  minHeight: '0',
                  minWidth: 'max-content',
                  justifyContent: 'start',
                  color: tabValue !== index ? '#9CA3AF' : '#1F2937',
                  lineHeight: '32px'
                }}
                role="tab"
                onClick={(): void => {
                  setTabValue(index);
                  onTabSelected && onTabSelected(tab.label);
                }}
                onMouseEnter={(): void => {
                  tab.onMouseEnter && tab.onMouseEnter();
                }}
              />
            </Box>
          );
        } else {
          return (
            <Tab
              label={tab.label}
              icon={tab.tabItemCount ? <div style={itemCountSx}>{tab.tabItemCount}</div> : ''}
              iconPosition="end"
              key={index}
              sx={{
                fontFamily: 'Poppins',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '18px',
                textTransform: 'none',
                minHeight: '64px',
                color: '#7E858E',
                lineHeight: '32px',
                '& .Mui-focusVisible': {
                  backgroundColor: 'red'
                },
                '& .Mui-selected': {
                  textTransform: 'none',
                  color: '#1d212c'
                }
              }}
              role="tab"
              onClick={(): void => {
                onTabSelected && onTabSelected(tab.label);
              }}
              onMouseEnter={(): void => {
                tab.onMouseEnter && tab.onMouseEnter();
              }}
            />
          );
        }
      } else {
        return <div></div>;
      }
    });
    return items;
  };

  const actionsClasses = desktopTabs()
    ? 'tw-main-view__actions tid-main-view__actions'
    : 'tw-main-view__actions tid-main-view__actions tw-main-view__actions--fullwidth';

  const tabClasses =
    matchesMobileOrTablet && !isDriverOrCarrier
      ? 'tw-main-view__actions-fullwidth tw-main-view__actions-mobile'
      : 'tw-main-view__actions-mobile';

  const showTabContent = (): JSX.Element[] | undefined => {
    const items = navItems?.map((tab: TwTabItemProps, index: number): JSX.Element => {
      return (
        <TabPanel value={tabValue} index={index} key={index} fullHeight={true}>
          {tab.content}
        </TabPanel>
      );
    });
    return items;
  };

  const showMobileDropdown = (): JSX.Element => {
    const currentTab = navItems?.[tabValue].label;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Button
          ref={anchorRef}
          onClick={handleDropdownToggle}
          sx={{
            fontFamily: 'Poppins',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '18px',
            textTransform: 'none',
            minHeight: '64px',
            color: '#1d212c',
            lineHeight: '32px',
            whiteSpace: 'nowrap'
          }}
        >
          {currentTab}
          {currentTab === 'Inbox' && inboxItemCount != 0 && (
            <ItemCounter selected={true}>{inboxItemCount}</ItemCounter>
          )}
          <KeyboardArrowDown />
        </Button>
        <Popper
          open={isMobileTabDropdownOpen}
          anchorEl={anchorRef.current}
          style={{ zIndex: 3 }}
          placement="bottom-start"
          transition
        >
          {({ TransitionProps, placement }): JSX.Element => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom'
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleDropdownClose}>
                  <MenuList>
                    {navItems?.map((item: TwTabItemProps, index: number): JSX.Element => {
                      return item.visible ? (
                        <MenuItem
                          key={index}
                          onClick={(e): void => {
                            handleItemClick(e, index);
                            onTabSelected && onTabSelected(item.label);
                          }}
                          onMouseEnter={(): void => {
                            item.onMouseEnter && item.onMouseEnter();
                          }}
                        >
                          <ListItemText>{item.label}</ListItemText>
                          {item.label === 'Inbox' && inboxItemCount != 0 && (
                            <ItemCounter selected={true}>{inboxItemCount}</ItemCounter>
                          )}
                        </MenuItem>
                      ) : (
                        <div></div>
                      );
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    );
  };

  return (
    <div className="tw-main-view__container">
      <div
        className="tw-main-view__wrapper tid-main-view__wrapper"
        style={{ paddingLeft: !matchesMobileOrTablet ? '64px' : 0 }}
      >
        <div
          className={`tw-main-view__header tid-main-view__header ${
            !!headerContent && tabsInHeader
              ? 'tw-main-view__header--column'
              : !!headerContent
              ? 'tw-main-view__header--auto'
              : ''
          }`}
        >
          {headerContent ? (
            headerContent
          ) : (
            <>
              {matchesMobileOrTablet && (
                <IconButton
                  key="main-view-action-mobile-btn"
                  sx={{ color: '#7E858E' }}
                  onClick={(): void => {
                    dispatch(openMobileMenu());
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {mainTitle &&
                navItems &&
                navItems.length > 1 &&
                !matchesMobileOrTablet &&
                !onlyShowMainTitle && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="tw-main-view__main-title">{mainTitle}</span>
                    <Divider
                      orientation="vertical"
                      sx={{ height: '20px', alignSelf: 'center', marginRight: '20px' }}
                      flexItem
                    />
                  </div>
                )}
              {mainTitle && onlyShowMainTitle && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="tw-main-view__main-title">{mainTitle}</span>
                </div>
              )}
              {matchesMobileOrTablet && enableMobileLoadInbox && showDropdown
                ? showMobileDropdown()
                : desktopTabs()
                ? showDesktopTabs()
                : !mainTitle && title && <span className="tw-main-view__title">{title}</span>}
              {actionButtons && actionButtons.length > 0 ? (
                <div className={`${actionsClasses} ${tabClasses}`}>
                  {actionButtons.map((action: JSX.Element, index: number) => (
                    <div
                      key={`main-view-action-btn-${index}`}
                      className="tw-main-view__actions-item"
                    >
                      {action}
                    </div>
                  ))}
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </div>
        {!isNotificationBannerAcknowledged && !window.location.pathname.includes('/settings') && (
          <TwNotificationBanner />
        )}

        {isCalendarOpen ? (
          <div
            className="tw-main-view__content tid-main-view__content"
            style={{
              overflowY: 'unset'
            }}
          >
            {showTabContent()}
            {children}
          </div>
        ) : (
          <Scrollbars autoHide autoHideTimeout={750} autoHideDuration={200}>
            <div
              className={`${
                tabsInHeader ? '' : 'tw-main-view__content__no-tabs'
              } tw-main-view__content tid-main-view__content`}
            >
              {showTabContent()}
              {children}
            </div>
          </Scrollbars>
        )}
      </div>
    </div>
  );
};

export default TwMainView;
